// @flow

import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Skeleton from '@material-ui/lab/Skeleton';
import { useShowAlert } from 'hooks';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { EntityRoutes } from 'routes/constants';
import API from 'services/API';
import { ContactIncludes } from 'services/includes';
import type { Map } from 'types';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import HiringAuthorityForm from 'UI/components/organisms/HiringAuthorityForm';
import HiringAuthoritySpecialtyForm from 'UI/components/organisms/HiringAuthoritySpecialtyForm';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { OptionRenderers, Renderers, Selectors } from 'UI/utils/renderers';

import { getPhonesSortedByDefaultPhone } from '../PhoneNumbersForm/utils';

type HiringAuthorityCreateFormProps = {
  joborderId: number,
  type: 'company' | 'joborder',
  isFromProfile?: boolean
};

const HiringAuthoritySelectForm = ({
  isFetching,
  joborderId,
  toggleIsFetching,
  type
}: HiringAuthorityCreateFormProps) => {
  const { showAlert } = useShowAlert();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const [comboValues, setComboValues] = useState<Map>({});

  const shouldShowKeywordTags = checkIfFeatureFlagEnabled(FeatureFlags.ContactKeywordTags);
  const isMultiplePhonesEnabled = checkIfFeatureFlagEnabled(
    FeatureFlags.HiringAuthoritiesMultiplePhones
  );
  const { register, errors, setValue, getValues, reset } = useFormContext();

  useEffect(() => {
    register({ name: 'hiring_authority_id' }, { required: 'Hiring Authority is required' });
    register({ name: 'existingHiringAuthority' });
    register({ name: 'isContact' });
    register({ name: 'hasMultiplePhones' });
  }, [register]);

  const handleComboChange = (name?: string, value: any) => {
    setComboValues((prevState: Map): Map => ({ ...prevState, [name]: value }));
    setValue(name, value ? value.id : value, true);
  };

  const preloadForm = (nameInfo: any) => {
    const {
      id,
      email,
      title,
      personalInformation: {
        first_name,
        last_name,
        nickname,
        contact: { phone, ext, mobile, personal_email } = {}
      } = {},
      specialty = {},
      subspecialty,
      position,
      link_profile: linkProfile,
      keywordTags = []
    } = nameInfo || {};

    const phones = getPhonesSortedByDefaultPhone(nameInfo, EntityType.Contact);

    reset({
      ...getValues(),
      id,
      first_name,
      last_name,
      nickname,
      specialty_id: specialty && specialty.id,
      subspecialty_id: subspecialty && subspecialty.id,
      position_id: position && position.id,
      specialty,
      subspecialty,
      position,
      title,
      work_email: email,
      work_phone: phone,
      ext,
      personal_email,
      personal_phone: mobile,
      phones,
      phone,
      link_profile: linkProfile,
      keywordTags
    });

    setComboValues((prevState: Map): Map => ({
      ...prevState,
      specialty_id: specialty,
      subspecialty_id: subspecialty,
      position_id: position
    }));
  };

  const fetchEntityAndPreloadForm = async selectedValue => {
    toggleIsFetching();
    try {
      const isContact = selectedValue.type === EntityType.ContactType.Contact;

      const queryParams = shouldShowKeywordTags ? `?includes=${ContactIncludes},keywordTags` : '';
      const endpoint = isContact
        ? `${Endpoints.Names}/${selectedValue.id}${queryParams}`
        : `${Endpoints.HiringAuthorities}/${selectedValue.id}`;

      const apiVersion = isMultiplePhonesEnabled ? 2 : 1;
      const response = await API.get(endpoint, { apiVersion });
      const hasPhones = !!response.data.phone_id;
      response.data.hasMultiplePhones = !!hasPhones;

      if (isContact) {
        response.data && preloadForm(response.data);
      }
      setValue('hasMultiplePhones', !!hasPhones);
      setValue('existingHiringAuthority', response.data);
    } catch (error) {
      showAlert({
        title: 'Error',
        error
      });
    } finally {
      toggleIsFetching();
    }
  };

  const handleHASelectContact = (name?: string, value: any) => {
    if (!value) {
      setComboValues({});
      reset({});
    } else {
      setValue('isContact', value.type === EntityType.ContactType.Contact);
      fetchEntityAndPreloadForm(value);
    }

    handleComboChange(name, value);
  };

  const hiringAuthorityComboProps =
    type === EntityType.Joborder
      ? {
          placeholder: 'Hiring Authority *',
          url: `${Endpoints.JobOrders}/${joborderId}/${Endpoints.AvailableHiringAuthorities}`,
          noOptionsText: 'No Hiring Authorities found for this Job Order',
          onSelect: handleComboChange,
          getOptionLabel: option => option.full_name,
          getOptionSelected: Selectors.byId,
          renderOption: OptionRenderers.displayHiringAuthority
        }
      : {
          placeholder: 'Search a Hiring Authority by name or email *',
          url: `${Endpoints.Search}`,
          displayKey: 'full_name',
          typeahead: true,
          typeaheadLimit: 25,
          typeaheadParams: {
            entityType: 'hiringAuthorityOrName'
          },
          groupBy: option => option.typeTitle,
          onSelect: handleHASelectContact,
          renderOption: OptionRenderers.displayHiringAuthority
        };

  const hasSpecialtyId = comboValues?.hiring_authority_id?.specialty_id;
  const isTypeInventoryOrHiringAuthority =
    comboValues.hiring_authority_id?.type === EntityType.ContactType.Inventory;
  const isTypeContact = comboValues.hiring_authority_id?.type === EntityType.ContactType.Contact;

  return (
    <div className="padding-x1 padding-y1">
      <AutocompleteSelect
        name="hiring_authority_id"
        selectedValue={comboValues.hiring_authority_id}
        error={!!errors.hiring_authority_id}
        errorText={errors.hiring_authority_id && errors.hiring_authority_id.message}
        {...hiringAuthorityComboProps}
      />
      {isFetching && (
        <>
          <Skeleton variant="rect" width={400} height={24} className="margin-right-bottom-1" />
          <Skeleton variant="rect" width={250} height={16} className="margin-right-bottom-1" />
          <Skeleton variant="rect" width={150} height={16} className="margin-right-bottom-1" />
        </>
      )}
      {!isFetching && !isTypeContact && hasSpecialtyId && (
        <>
          <div className="margin-y1">
            {Renderers.displayBasicInfoEntity(comboValues.hiring_authority_id)}
          </div>
          {Renderers.externalLink(
            EntityRoutes.HiringAuthorityProfile.replace(':id', comboValues.hiring_authority_id.id),
            'View Hiring Authority'
          )}
        </>
      )}
      {!isFetching && isTypeInventoryOrHiringAuthority && !hasSpecialtyId && (
        <HiringAuthoritySpecialtyForm specialtyId={hasSpecialtyId} />
      )}
      {!isFetching && isTypeContact && (
        <>
          <p>Register the required data to add this Hiring Authority</p>
          <HiringAuthorityForm initialValues={comboValues} />
        </>
      )}
    </div>
  );
};

HiringAuthoritySelectForm.defaultProps = {
  joborderId: null,
  isFromProfile: false
};

export default HiringAuthoritySelectForm;
